import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { SERVICES_INDEX, SERVICES } from "@/data/services";
import SEO, { breadcrumbJsonLd, serviceJsonLd } from "@/components/SEO";

export default function ServiceDetail() {
  const { slug } = useParams();
  const s = SERVICES_INDEX[slug];

  if (!s) {
    return (
      <div className="container-x py-32 text-center">
        <h1 className="font-display text-3xl font-bold">Service not found</h1>
        <Link to="/services" className="btn-primary mt-8 inline-flex">All services</Link>
      </div>
    );
  }

  const Icon = s.icon;
  const related = SERVICES.filter((x) => x.slug !== s.slug).slice(0, 3);
  const jsonLd = [
    serviceJsonLd({ name: s.title, description: s.summary, slug: s.slug }),
    breadcrumbJsonLd([
      { name: "Home", path: "/" },
      { name: "Services", path: "/services" },
      { name: s.title, path: `/services/${s.slug}` },
    ]),
  ];

  return (
    <div data-testid="service-detail-page">
      <SEO
        title={`${s.title} — Independent Texas Environmental Consulting`}
        description={s.summary}
        path={`/services/${s.slug}`}
        jsonLd={jsonLd}
      />
      <section className="bg-[#0F172A] text-white py-20 md:py-24">
        <div className="container-x">
          <Link to="/services" className="text-white/60 hover:text-white text-sm inline-flex items-center gap-2 mb-8">
            <ArrowLeft className="w-4 h-4" /> All services
          </Link>
          <div className="flex items-start gap-6">
            <div className="w-16 h-16 rounded-2xl bg-[#7C3AED]/20 grid place-items-center shrink-0">
              <Icon className="w-7 h-7 text-[#F59E0B]" />
            </div>
            <div>
              <div className="overline text-[#F59E0B] mb-3">Service</div>
              <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight leading-tight">{s.title}</h1>
              <p className="mt-5 text-lg text-white/70 max-w-2xl leading-relaxed">{s.summary}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad bg-white">
        <div className="container-x grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-12">
            {[
              { h: "Why it matters", b: s.why },
              { h: "When is it needed?", b: s.when },
              { h: "Who needs it?", b: s.who },
              { h: "How we perform it", b: s.how },
              { h: "Laboratory methods", b: s.lab },
              { h: "Interpretation", b: s.interpretation },
            ].map((blk) => (
              <div key={blk.h}>
                <div className="overline mb-3">{blk.h}</div>
                <p className="text-lg text-slate-700 leading-relaxed">{blk.b}</p>
              </div>
            ))}
          </div>

          <aside className="space-y-8">
            <div className="card-soft p-7">
              <div className="overline mb-4">Equipment used</div>
              <ul className="space-y-3">
                {s.equipment.map((e) => (
                  <li key={e} className="flex items-start gap-3 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-[#7C3AED] mt-0.5" /> {e}
                  </li>
                ))}
              </ul>
            </div>
            <div className="card-soft p-7">
              <div className="overline mb-4">Standards applied</div>
              <ul className="space-y-3">
                {s.standards.map((std) => (
                  <li key={std} className="text-sm text-slate-700">{std}</li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl bg-[#0F172A] text-white p-7">
              <div className="font-display font-bold text-lg mb-2">Schedule this service</div>
              <p className="text-white/70 text-sm mb-5">Most assessments scheduled within 48–72 hours.</p>
              <Link to="/schedule" data-testid="service-schedule-cta" className="btn-primary w-full justify-center">
                Schedule <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>
          </aside>
        </div>
      </section>

      <section className="section-pad bg-[#F8FAFC]">
        <div className="container-x">
          <div className="overline mb-4">Related services</div>
          <h2 className="font-display text-3xl font-bold text-[#0F172A] mb-12">Continue exploring</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((r) => {
              const RIcon = r.icon;
              return (
                <Link key={r.slug} to={`/services/${r.slug}`} data-testid={`related-service-${r.slug}`} className="card-soft block p-7">
                  <RIcon className="w-6 h-6 text-[#7C3AED] mb-4" />
                  <div className="font-display font-bold text-[#0F172A] mb-2">{r.title}</div>
                  <div className="text-sm text-slate-600 leading-relaxed">{r.summary}</div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
